import { useCallback } from 'react';
import React from 'react';

import { Box, Icon, Pressable, Text } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { usePaymentContext } from 'features/payment';
import { IPaymentMethod, IRevaultInformation } from 'features/payment/types';
import { shouldReVault } from 'features/payment/utils';
import { useIntl } from 'react-intl';

import ActionButton, { ActionButtonSizes, ActionButtonVariants } from 'components/action-button';
import {
  getSortedMethods,
  isCreditCardAndGoogleApplePaymentMethod,
} from 'components/payment-method-option/utils';
import { classifyPaymentMethods } from 'components/payment-method/utils';
import { emptyCartContext, useCartContext } from 'state/cart';
import { useLocale } from 'state/intl';
import { useUIContext } from 'state/ui';
import { theme } from 'styles/configure-theme';
import { APPROXIMATE_DRAWER_ACTION_SHEET_ANIMATION_TIME_MS } from 'utils/constants';
import { isAndroid, isWeb } from 'utils/environment';
import { Region } from 'utils/environment/types';
import noop from 'utils/noop';
import { routes } from 'utils/routing';

import { useOrderPayment } from '../use-order-payment';

import { PaymentMethod } from './payment-method';
import { HorizontalDrawerContainer } from './styled';
import { IPaymentMethodSelectorProps } from './types';

export function PaymentMethodSelectorOptions({
  onConfirm,
  methods,
  selectedMethodId,
  isReloadDrawer,
  onClose,
  reloadSelectedMethodId = '',
  setIsGiftCardVisible = noop,
}: Omit<IPaymentMethodSelectorProps, 'isOpen'>) {
  const { formatMessage } = useIntl();
  const { selectedMethod } = classifyPaymentMethods(
    methods,
    isReloadDrawer ? reloadSelectedMethodId : selectedMethodId
  );
  const sortedMethods = getSortedMethods(methods);
  const { formatCurrencyForLocale } = useUIContext();

  const {
    setCheckoutPaymentMethodId,
    setDefaultPaymentMethodId,
    getPrepaidPaymentMethod,
    setPrepaidReloadPaymentMethodId,
    setDefaultReloadPaymentMethodId,
    isOrbital: isOrbitalPaymentEnabled,
    getBalanceFromPaymentMethods,
    setAccountToDelete,
  } = usePaymentContext();
  const existsPrepaidMethod = getPrepaidPaymentMethod();
  const { handlePaymentMethodSelected } = useOrderPayment();
  const { region } = useLocale();
  const isCARegion = region === Region.CA;
  const router = useRouter();

  // TODO if we move the pressable inside the payment method component this logic could be removed
  // there is a similar logic in the payment method component
  const getAccessibilityLabelByMethod = useCallback(
    (method: IPaymentMethod) => {
      if (method.credit) {
        const methodName = method.credit.cardType || formatMessage({ id: 'card' });
        return `${methodName} ${method.credit.alias}`;
      }
      if (method.prepaid) {
        const amount = getBalanceFromPaymentMethods(method);
        const methodName = formatMessage({ id: 'giftCard' });
        const amountFormatted = formatCurrencyForLocale(amount);
        return `${methodName} ${amountFormatted} `;
      }

      const methodTypes = {
        cash: formatMessage({ id: 'cash' }),
        paypal: formatMessage({ id: 'paypal' }),
        venmo: formatMessage({ id: 'venmo' }),
        cashapp: formatMessage({ id: 'cashAppPay' }),
      };

      for (const [key, value] of Object.entries(methodTypes)) {
        if (method[key]) {
          return value;
        }
      }

      return '';
    },
    [formatCurrencyForLocale, formatMessage, getBalanceFromPaymentMethods]
  );

  const filteredMap = isReloadDrawer
    ? Object.entries(sortedMethods)
        .filter(([, value]) => isCreditCardAndGoogleApplePaymentMethod(value))
        .map(([, value]) => value)
    : sortedMethods;

  const handleOnPress = useCallback(
    (method: IPaymentMethod) => {
      const accountIdentifier = method.accountIdentifier ?? method.fdAccountId ?? '';
      if (isReloadDrawer) {
        setPrepaidReloadPaymentMethodId(accountIdentifier || '');
        setDefaultReloadPaymentMethodId(accountIdentifier || '');
      } else {
        handlePaymentMethodSelected(accountIdentifier || '');
        setCheckoutPaymentMethodId(accountIdentifier || '');
        setDefaultPaymentMethodId(accountIdentifier || '');
      }
      onConfirm();
    },
    [
      onConfirm,
      handlePaymentMethodSelected,
      isReloadDrawer,
      setCheckoutPaymentMethodId,
      setDefaultPaymentMethodId,
      setDefaultReloadPaymentMethodId,
      setPrepaidReloadPaymentMethodId,
    ]
  );

  const isInCart = useCartContext() !== emptyCartContext;

  return (
    <>
      {filteredMap.map((method: IPaymentMethod) => {
        const accountIdentifier = method.accountIdentifier ?? method.fdAccountId ?? '';
        const revaultInformation: IRevaultInformation = shouldReVault({
          accountIdentifier: accountIdentifier || '',
          isOrbitalPaymentEnabled,
          methods,
        });
        const { isInvalid = false, isExpired = false } = revaultInformation ?? {};
        const isRevaultRequired = isInvalid || isExpired;
        const isSelectedMethod =
          accountIdentifier === selectedMethod?.fdAccountId ||
          accountIdentifier === selectedMethod?.accountIdentifier;

        if (isSelectedMethod) {
          return (
            <Box flexDirection="row" width="full" justifyContent="space-between">
              {
                <HorizontalDrawerContainer>
                  <PaymentMethod method={selectedMethod || null} />
                </HorizontalDrawerContainer>
              }
              <Icon
                variant="check"
                title="check"
                color={theme.token('icon-default')}
                size={5}
                top={4}
                right={0}
                position="absolute"
              />
            </Box>
          );
        }

        if (method.prepaid && isCARegion) {
          return null;
        }

        if (isRevaultRequired) {
          return (
            <>
              <HorizontalDrawerContainer
                isRevaultRequired={isRevaultRequired}
                key={`method-${method.fdAccountId}`}
              >
                <PaymentMethod method={method || null} />
              </HorizontalDrawerContainer>
              <HorizontalDrawerContainer
                isRevaultRequired={isRevaultRequired}
                key={`revault-${method.fdAccountId}`}
                justifyContent="space-between"
              >
                <Box mt="$2" flexDirection="row">
                  <Icon variant="warning" size="xs" />
                  <Text ml="$3" pb="$2" fontSize="xs">
                    {formatMessage({ id: 'expiredCard' })}
                  </Text>
                </Box>
                <Box>
                  <ActionButton
                    onPress={() => {
                      setAccountToDelete(accountIdentifier);
                      router.navigate(`${routes.addCard}?isInCart=${isInCart}`);
                    }}
                    variant={ActionButtonVariants.OUTLINE}
                    size={ActionButtonSizes.SMALL}
                    width="$24"
                  >
                    {formatMessage({ id: 'update' })}
                  </ActionButton>
                </Box>
              </HorizontalDrawerContainer>
            </>
          );
        }

        return (
          <Pressable
            accessibilityLabel={getAccessibilityLabelByMethod(method)}
            accessibilityRole="button"
            key={`unselectedId-${method.accountIdentifier}`}
            width="full"
            onPress={() => handleOnPress(method)}
          >
            <HorizontalDrawerContainer key={`method-${method.fdAccountId}`}>
              <PaymentMethod method={method || null} />
            </HorizontalDrawerContainer>
          </Pressable>
        );
      })}

      <Pressable
        accessibilityLabel={formatMessage({ id: 'addNewCard' })}
        role="button"
        width="full"
        onPress={() => {
          setAccountToDelete('');
          if (isAndroid || isWeb) {
            // we need to close here because otherwise the nativebase drawer stays on top of add card regardless of zindex on android/web and can cause lockups
            onClose?.();
            setTimeout(() => {
              router.navigate(`${routes.addCard}?isInCart=${isInCart}`);
            }, APPROXIMATE_DRAWER_ACTION_SHEET_ANIMATION_TIME_MS);
          } else {
            // ios add card is a separate modal screen so it doesn't have the issue
            router.navigate(`${routes.addCard}?isInCart=${isInCart}`);
          }
        }}
      >
        <HorizontalDrawerContainer isStrongBorder={true}>
          <Icon accessibilityHidden variant="emptyCreditCard" size="md" />
          <Text color={theme.token('text-default')}>{formatMessage({ id: 'addNewCard' })}</Text>
        </HorizontalDrawerContainer>
      </Pressable>

      {!isCARegion && (
        <Pressable
          accessibilityLabel={
            (existsPrepaidMethod && formatMessage({ id: 'transferBalance' })) ||
            formatMessage({ id: 'addGiftCard' })
          }
          role="button"
          width="full"
          onPress={() => {
            if (isAndroid || isWeb) {
              // we need to close here because otherwise the nativebase drawer stays on top of add card regardless of zindex on android/web and can cause lockups
              onClose?.();
              setTimeout(() => {
                setIsGiftCardVisible(true);
              }, APPROXIMATE_DRAWER_ACTION_SHEET_ANIMATION_TIME_MS);
            } else {
              setIsGiftCardVisible(true);
            }
          }}
        >
          <HorizontalDrawerContainer>
            <Icon accessibilityHidden variant="giftCard" size="md" />
            <Text color={theme.token('text-default')}>
              {(existsPrepaidMethod && formatMessage({ id: 'transferBalance' })) ||
                formatMessage({ id: 'addGiftCard' })}
            </Text>
          </HorizontalDrawerContainer>
        </Pressable>
      )}
    </>
  );
}
